import { useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  QuickStudyDraftView,
  QuickStudyFindingsMessage,
  QuickStudyResultsDocument,
  QuickStudyResultsView,
} from "@digital-twins/shared-types";
import { MarkdownBody } from "@design-system/components/MarkdownBody";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { quickStudiesApi } from "@/lib/api/quick-study-native";
import type { StudyDocQuote } from "@/components/study-doc";
import {
  initialsFromDisplayName,
  normalizeStudyDocQuote,
} from "@/components/study-doc/enrichStudyDocQuote";
import { parseEmbedTokens } from "@/components/artifact-editor/parseEmbedTokens";
import { SurveyFiguresProvider } from "@/features/studies/report/figures/SurveyFiguresProvider";
import { buildBlurbLine, inferAudienceType } from "@/lib/utils/twin-blurb";
import { ArtifactPreviewCard } from "./ThreadRunPane/ArtifactPreviewCard";
import { ThreadArtifactFullscreenView } from "./ThreadRunPane/ThreadArtifactFullscreenView";
import {
  QuickStudyReportQuestionPlot,
  QuickStudyResultsQuestions,
} from "./QuickStudyResultsQuestions";
import type { QuickStudyReportQuoteEntry } from "./QuickStudyResultsQuestions";
import { ThreadReportCard } from "./ThreadReportCard";
import { useQuickRunReportData } from "./useQuickRunReportData";
import type { ThreadArtifactItem } from "./ThreadRunPane/collectThreadArtifacts";

/** A13 documents carry their report and render snapshot; they draw with no request. */
function isReportDocument(
  document: QuickStudyResultsDocument | undefined,
): document is QuickStudyResultsDocument &
  Required<Pick<QuickStudyResultsDocument, "snapshot">> {
  return (
    document?.schemaVersion === "quick-study-results-document.v2" &&
    document.snapshot != null
  );
}

/** `{{quote:id}}` lookups from the snapshot's cited quotes and their saved speakers. */
function buildReportQuotes(
  document: QuickStudyResultsDocument,
): Record<string, StudyDocQuote> {
  const quotes: Record<string, StudyDocQuote> = {};
  for (const question of document.snapshot?.questions ?? []) {
    for (const quote of question.qualitative?.quoteCandidates ?? []) {
      const speaker =
        quote.respondentIdentityVerified !== false && quote.respondentId
          ? document.quoteSpeakers[quote.respondentId]
          : undefined;
      const name = speaker?.name ?? "";
      quotes[quote.id] = {
        text: quote.text,
        name,
        initials: initialsFromDisplayName(name),
        blurb: speaker
          ? buildBlurbLine(
              speaker.displayProfile,
              inferAudienceType(speaker.displayProfile),
            ) || undefined
          : undefined,
        segmentLabel: speaker?.segmentLabels[0],
      };
    }
  }
  return quotes;
}

/** Quote ids the document's text already places as `{{quote:id}}`. */
function placedQuoteIds(markdown: string): Set<string> {
  return new Set(
    parseEmbedTokens(markdown).flatMap((segment) =>
      segment.kind === "quote" ? [segment.id] : [],
    ),
  );
}

/**
 * Per question, its snapshot quotes as gallery entries, only for a question
 * none of whose quotes the text places (the writer's placement wins, so no
 * quote shows twice). Unnamed quotes are left out, as the text's are.
 */
export function buildUnplacedQuestionQuotes(
  document: QuickStudyResultsDocument,
  quotes: Record<string, StudyDocQuote>,
): Record<string, Array<QuickStudyReportQuoteEntry>> {
  const placed = placedQuoteIds(document.markdown);
  const byQuestion: Record<string, Array<QuickStudyReportQuoteEntry>> = {};
  for (const question of document.snapshot?.questions ?? []) {
    const ids = (question.qualitative?.quoteCandidates ?? []).map(
      (quote) => quote.id,
    );
    if (ids.length === 0 || ids.some((id) => placed.has(id))) {
      continue;
    }
    const entries = ids.flatMap((id) => {
      const named = quotes[id] ? normalizeStudyDocQuote(quotes[id]) : null;
      return named && named.text.trim() ? [{ id, quote: named }] : [];
    });
    if (entries.length > 0) {
      byQuestion[question.questionId] = entries;
    }
  }
  return byQuestion;
}

/** Compact immutable publication; the existing document reader owns full-screen rendering. */
export function ThreadQuickStudyFindingsMessage({
  findings,
  threadToken,
  text = "",
}: {
  findings: QuickStudyFindingsMessage;
  threadToken: string;
  text?: string;
}) {
  const { t } = useTranslation("app");
  const [params, setParams] = useSearchParams();
  const open = params.get("quickDocument") === findings.runToken;
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState<{
    evidence: QuickStudyResultsView;
    draft: QuickStudyDraftView;
  } | null>(null);
  const [failed, setFailed] = useState(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  const document = findings.document;
  const isReport = isReportDocument(document);
  const title = document?.title || text || t("threads.quickStudy.title");
  const markdown =
    document?.markdown ||
    [
      `# ${title}`,
      ...findings.questions.map((_, index) => `{{plot:question-${index + 1}}}`),
    ].join("\n\n");
  const item = useMemo<ThreadArtifactItem>(
    () => ({
      key: `quick-study-document:${findings.runToken}`,
      title,
      body: markdown,
      slug: "quick_study_results",
      stepId: findings.runToken,
      stepIndex: 0,
      artifactIndex: 0,
    }),
    [findings.runToken, title, markdown],
  );
  useEffect(() => {
    // A13 documents render from the saved message; only v1 fetches on open.
    if (!open || isReport) return;
    let current = true;
    setFailed(false);
    setLoaded(null);
    void Promise.all([
      quickStudiesApi.findings(
        threadToken,
        findings.draftToken,
        findings.runToken,
      ),
      quickStudiesApi.read(threadToken, findings.draftToken, findings.runToken),
    ])
      .then(([evidence, draft]) => {
        if (
          evidence.runToken !== findings.runToken ||
          draft.activeRun?.runToken !== findings.runToken ||
          draft.revision !== findings.revision
        )
          throw new Error("Saved document run identity changed");
        if (current)
          setLoaded({
            evidence: {
              ...evidence,
              quoteSpeakers: document?.quoteSpeakers ?? evidence.quoteSpeakers,
            },
            draft,
          });
      })
      .catch(() => {
        if (current) setFailed(true);
      });
    return () => {
      current = false;
    };
  }, [
    open,
    isReport,
    attempt,
    threadToken,
    findings.draftToken,
    findings.runToken,
    findings.revision,
    document,
  ]);
  function openDocument() {
    returnFocus.current =
      window.document.activeElement instanceof HTMLElement
        ? window.document.activeElement
        : null;
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("quickDocument", findings.runToken);
      return next;
    });
  }
  function close() {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("quickDocument");
        return next;
      },
      { replace: true },
    );
    requestAnimationFrame(() => returnFocus.current?.focus());
  }
  const reportQuotes = useMemo(
    () => (isReport ? buildReportQuotes(document) : undefined),
    [isReport, document],
  );
  // The open document reads the run's frozen survey: helper text and segment options.
  const runData = useQuickRunReportData({
    threadToken,
    draftToken: findings.draftToken,
    runToken: findings.runToken,
    isEnabled: open && isReport,
  });
  const quickRun = useMemo(
    () => ({
      threadToken,
      draftToken: findings.draftToken,
      runToken: findings.runToken,
    }),
    [threadToken, findings.draftToken, findings.runToken],
  );
  const unplacedQuotes = useMemo(
    () =>
      isReport && reportQuotes
        ? buildUnplacedQuestionQuotes(document, reportQuotes)
        : {},
    [isReport, document, reportQuotes],
  );
  /** The inline preview draws the saved figures; the open document adds its extras. */
  function buildReportPlots(isOpenDocument: boolean) {
    if (!isReport) {
      return null;
    }
    return Object.fromEntries(
      findings.questions.map((question, index) => [
        `question-${index + 1}`,
        <QuickStudyReportQuestionPlot
          key={question.questionId}
          question={question}
          qualitative={
            document.snapshot.questions.find(
              (item) => item.questionId === question.questionId,
            )?.qualitative
          }
          run={isOpenDocument ? runData : null}
          quotes={isOpenDocument ? unplacedQuotes[question.questionId] : undefined}
        />,
      ]),
    );
  }
  const reportPlots = useMemo(
    () => buildReportPlots(false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isReport, findings.questions, document],
  );
  const documentReportPlots = useMemo(
    () => buildReportPlots(true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isReport, findings.questions, document, runData, unplacedQuotes],
  );
  const plots = reportPlots ?? Object.fromEntries(
    (loaded?.draft.instrument?.questions ?? findings.questions).map(
      (question, index) => [
        `question-${index + 1}`,
        loaded?.draft.instrument ? (
          <QuickStudyResultsQuestions
            key={index}
            evidence={loaded.evidence}
            questions={[loaded.draft.instrument.questions[index]]}
            onRetry={() => setAttempt((value) => value + 1)}
          />
        ) : failed ? (
          <div role="alert">
            <Text size="sm">{t("threads.quickStudy.resultsFailed")}</Text>
            <Button
              variant="outline"
              onClick={() => setAttempt((value) => value + 1)}
            >
              {t("threads.quickStudy.retry")}
            </Button>
          </div>
        ) : (
          <Text size="sm" color="muted" role="status">
            {t("threads.quickStudy.loadingResults")}
          </Text>
        ),
      ],
    ),
  );
  return (
    <div
      className="space-y-4"
      data-testid="thread-quick-study-findings-message"
      data-quick-study-run={findings.runToken}
      data-quick-study-revision={findings.revision}
    >
      {document?.insight ? (
        <MarkdownBody content={document.insight} density="thread" />
      ) : null}
      {document?.writingFailure ? (
        <Text size="sm" color="muted" role="status">
          {t(
            document.writingFailure === "insight_unavailable"
              ? "threads.quickStudy.insightUnavailable"
              : "threads.quickStudy.summaryUnavailable",
          )}
        </Text>
      ) : null}
      {isReport ? (
        <ThreadReportCard
          report={{ publicId: `quick-${findings.runToken}`, title }}
          item={item}
          onExpand={openDocument}
          onFullscreen={openDocument}
          plots={plots}
          quotes={reportQuotes}
          openLabel={t("threads.quickStudy.openDocument")}
        />
      ) : (
      <ArtifactPreviewCard
        item={item}
        className="rounded-2xl border border-border bg-background p-5"
        canEdit={false}
        plots={{}}
        onExpand={openDocument}
      >
        <Button variant="outline" onClick={openDocument}>
          {t("threads.quickStudy.openDocument")}
        </Button>
      </ArtifactPreviewCard>
      )}
      {open && isReport ? (
        <SurveyFiguresProvider quickRun={quickRun}>
          <ThreadArtifactFullscreenView
            item={item}
            plots={documentReportPlots ?? plots}
            quotes={reportQuotes}
            onClose={close}
          />
        </SurveyFiguresProvider>
      ) : null}
      {open && !isReport ? (
        <ThreadArtifactFullscreenView
          item={item}
          plots={plots}
          quotes={reportQuotes}
          onClose={close}
        />
      ) : null}
    </div>
  );
}
